import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, Video, Grid, LogOut, Menu, X, Coins, User, ChevronDown, UserCircle, LayoutDashboard, Home } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';

const navLinks = [
  { name: 'Immagini', href: '/image-generator', icon: Image },
  { name: 'Video', href: '/video-generator', icon: Video },
  { name: 'Galleria', href: '/gallery', icon: Grid },
];

const navGlass = {
  backgroundColor: 'rgba(8,8,18,0.55)',
  backdropFilter: 'blur(20px) saturate(140%)',
  WebkitBackdropFilter: 'blur(20px) saturate(140%)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
};

const drawerGlass = {
  background: 'rgba(8,8,20,0.97)',
  border: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(40px) saturate(150%)',
  WebkitBackdropFilter: 'blur(40px) saturate(150%)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
};

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { user, logout } = useAuth();
  const { credits } = useCredits();
  const location = useLocation();

  const displayCredits = typeof credits === 'number' && credits > 999999 ? '∞' : credits;

  const handleLogout = async () => {
    setMobileOpen(false);
    setDropOpen(false);
    await logout();
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className="sticky top-0 z-50 px-3 pt-3"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        <div
          className="max-w-7xl mx-auto px-4 flex items-center justify-between rounded-2xl"
          style={{ ...navGlass, height: 60 }}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            <img src="/tente.ico" alt="Gaked AI" className="w-7 h-7 object-contain" />
            <span
              className="text-[17px] font-bold tracking-tight"
              style={{ color: '#fff', letterSpacing: '-0.02em' }}
            >
              Gaked AI
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className="relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
                  style={{
                    color: active ? '#fff' : 'rgba(255,255,255,0.58)',
                    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                  }}
                >
                  <Icon size={15} />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-2">
            {/* Credits */}
            <Link
              to="/credits"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
                color: 'rgba(255,255,255,0.78)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            >
              <Coins size={13} style={{ color: 'rgba(255,220,80,0.9)' }} />
              <span>{displayCredits}</span>
            </Link>

            {/* User dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropOpen(!dropOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  color: 'rgba(255,255,255,0.78)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                <div
                  className="w-5 h-5 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  <User size={11} color="#fff" />
                </div>
                <span className="max-w-[80px] truncate">{user?.username || 'Utente'}</span>
                <motion.div animate={{ rotate: dropOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown size={13} />
                </motion.div>
              </button>

              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-52 rounded-2xl overflow-hidden"
                    style={{
                      background: 'rgba(8,8,20,0.85)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      backdropFilter: 'blur(24px)',
                      WebkitBackdropFilter: 'blur(24px)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    }}
                  >
                    <div className="p-1.5">
                      <div className="px-3 py-2.5 mb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        <p className="text-xs font-semibold text-white truncate">{user?.username}</p>
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.38)' }}>{user?.email}</p>
                      </div>
                      {[
                        { to: '/', icon: Home, label: 'Home' },
                        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                        { to: '/profile', icon: UserCircle, label: 'Profilo' },
                      ].map(({ to, icon: Icon, label }) => (
                        <Link
                          key={to}
                          to={to}
                          onClick={() => setDropOpen(false)}
                          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm transition-all duration-150"
                          style={{ color: 'rgba(255,255,255,0.62)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.62)'; }}
                        >
                          <Icon size={14} />
                          {label}
                        </Link>
                      ))}
                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4, paddingTop: 4 }}>
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm transition-all duration-150"
                          style={{ color: 'rgba(255,80,80,0.8)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,60,60,0.08)'; e.currentTarget.style.color = 'rgba(255,100,100,1)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,80,80,0.8)'; }}
                        >
                          <LogOut size={14} />
                          Esci
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile hamburger — large touch target */}
          <button
            onClick={() => setMobileOpen(prev => !prev)}
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200"
            style={{
              background: mobileOpen ? 'rgba(255,255,255,0.10)' : 'transparent',
              border: mobileOpen ? '1px solid rgba(255,255,255,0.14)' : '1px solid transparent',
            }}
            aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mobileOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X size={20} color="rgba(255,255,255,0.9)" />
                </motion.div>
              ) : (
                <motion.div
                  key="open"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Menu size={20} color="rgba(255,255,255,0.85)" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* ── Mobile overlay + drawer — fixed, outside sticky nav ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
              onClick={closeMobile}
            />

            {/* Drawer */}
            <motion.div
              key="mobile-drawer"
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.97 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-3 right-3 z-50 md:hidden rounded-2xl overflow-hidden"
              style={{ top: 76, ...drawerGlass }}
            >
              {/* User info row */}
              <div
                className="flex items-center gap-3 px-5 py-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                >
                  <User size={15} color="rgba(255,255,255,0.75)" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{user?.username || 'Utente'}</p>
                  <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.36)' }}>{user?.email}</p>
                </div>
                <Link
                  to="/credits"
                  onClick={closeMobile}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl flex-shrink-0"
                  style={{
                    background: 'rgba(255,220,80,0.08)',
                    border: '1px solid rgba(255,220,80,0.18)',
                  }}
                >
                  <Coins size={12} style={{ color: 'rgba(255,220,80,0.9)' }} />
                  <span className="text-xs font-bold" style={{ color: 'rgba(255,220,80,0.9)' }}>{displayCredits}</span>
                </Link>
              </div>

              {/* Links */}
              <div className="p-3 space-y-0.5">
                {/* Main nav links */}
                {navLinks.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={closeMobile}
                      className="flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-150"
                      style={{
                        color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                        background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
                        border: active ? '1px solid rgba(59,130,246,0.22)' : '1px solid transparent',
                      }}
                    >
                      <Icon size={17} style={{ color: active ? '#3b82f6' : 'rgba(255,255,255,0.45)' }} />
                      {item.name}
                    </Link>
                  );
                })}

                <div className="h-px mx-1 my-2" style={{ background: 'rgba(255,255,255,0.07)' }} />

                {/* Secondary links */}
                {[
                  { to: '/', icon: Home, label: 'Home' },
                  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                  { to: '/profile', icon: UserCircle, label: 'Profilo' },
                ].map(({ to, icon: Icon, label }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={closeMobile}
                    className="flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150"
                    style={{ color: 'rgba(255,255,255,0.50)' }}
                  >
                    <Icon size={16} style={{ color: 'rgba(255,255,255,0.35)' }} />
                    {label}
                  </Link>
                ))}

                <div className="h-px mx-1 my-2" style={{ background: 'rgba(255,255,255,0.07)' }} />

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3.5 w-full px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-150"
                  style={{ color: 'rgba(255,80,80,0.85)' }}
                >
                  <LogOut size={17} style={{ color: 'rgba(255,80,80,0.65)' }} />
                  Esci dall'account
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
